/**
 * Data Transformer Utilities
 * Reusable functions to transform API data for table display
 * Reduces code duplication across components
 */

/**
 * Creates a standard field object with default properties
 * @param {*} data - The field data
 * @param {string} variant - The field variant (text, date, status, etc.)
 * @param {Object} options - Additional options
 * @returns {Object} - Formatted field object
 */
export const createField = (data, variant = "text", options = {}) => {
  const {
    className = "",
    sticky = false,
    customCases = null,
    ...restOptions
  } = options;

  return {
    data,
    className,
    sticky,
    variant,
    customCases,
    ...restOptions
  };
};

/**
 * Common field transformers for frequently used fields
 */
export const fieldTransformers = {
  // ID field transformer
  id: (item, idKey = "uuid") => createField(item[idKey], "text"),
  
  // Name field transformer
  name: (item, nameKey = "name") => createField(item[nameKey], "name"),
  
  // Description field transformer
  description: (item, descKey = "description") => createField(item[descKey], "text"),
  
  // Abbreviation field transformer
  abbreviation: (item, abrvKey = "abrv") => createField(item[abrvKey], "text"),
  
  // Date field transformer
  createdAt: (item, dateKey = "created_at") => createField(item[dateKey], "date-time"),
  
  // Date only transformer
  date: (item, dateKey = "created_at") => createField(item[dateKey], "date"),
  
  // Status field transformer
  status: (item, customCases = null) => createField(
    item.status ? 1 : 0, 
    "status", 
    { customCases }
  ),
  
  // Actions field transformer
  actions: (item, idKey = "id") => createField(item[idKey], "text"),
  
  // Phone field transformer
  phone: (item, phoneKey = "phone") => createField(item[phoneKey], "text"),
  
  // Number field transformer
  number: (item, numberKey, defaultValue = "-") => createField(
    item[numberKey] || defaultValue, 
    "number"
  ),
  
  // Profile field transformer (for staff/user profiles)
  profile: (item, config = {}) => {
    const {
      nameKey = "name",
      lastNameKey = "last_name", 
      emailKey = "email",
      imageKey = "image",
      linkPrefix = "",
      uuidKey = "uuid"
    } = config;
    
    return createField({
      name: `${item[nameKey]} ${item[lastNameKey] ? item[lastNameKey] : ""}`,
      email: item[emailKey],
      image: item[imageKey] || "",
      link: linkPrefix ? `${linkPrefix}/${item[uuidKey]}` : "#"
    }, "profile");
  },
  
  // Select field transformer (for dropdown options)
  select: (item, selectKey, options = [], config = {}) => {
    const { 
      labelKey = "label", 
      valueKey = "value",
      defaultLabel = ""
    } = config;
    
    const selectedOption = options.find(option => option[valueKey] === item[selectKey]);
    
    return createField(
      selectedOption?.[labelKey] || defaultLabel,
      "select",
      {
        value: item[selectKey],
        options
      }
    );
  },
  
  // Checkbox field transformer (converts boolean to 1/0 for backend)
  checkbox: (item, checkboxKey, defaultValue = 0) => createField(
    item[checkboxKey] ? 1 : 0,
    "checkbox"
  )
};

/**
 * Creates a complete data transformer function
 * @param {Object} fieldMappings - Object mapping field names to transformer configs
 * @param {Function} actionButtonCreator - Function to create action buttons
 * @returns {Function} - Complete data transformer function
 * 
 * Example usage:
 * const transformer = createDataTransformer({
 *   id: { transformer: fieldTransformers.id, params: ["uuid"] },
 *   name: { transformer: fieldTransformers.name },
 *   status: { transformer: fieldTransformers.status, params: [customCases] }
 * }, (transformedData) => createActionButtons(...));
 */
export const createDataTransformer = (fieldMappings, actionButtonCreator = null) => {
  return (data) => {
    if (!Array.isArray(data)) {
      return { data: [], action: {} };
    }

    const transformedData = data.map((item, index) => {
      const transformedItem = { ...item };
      
      // Apply field transformations
      Object.keys(fieldMappings).forEach(fieldName => {
        const { transformer, params = [] } = fieldMappings[fieldName];
        
        if (typeof transformer === "function") {
          transformedItem[fieldName] = transformer(item, ...params);
        }
      });

      return transformedItem;
    });

    // Create action buttons if creator function is provided
    const actionButton = actionButtonCreator ? actionButtonCreator(transformedData) : {};

    return {
      data: transformedData,
      action: actionButton
    };
  };
};

/**
 * Predefined transformer configurations for common use cases
 */
export const presetTransformers = {
  // Basic CRUD entity transformer (id, name, description, status, actions)
  basicEntity: (customCases = null, idKey = "uuid") => ({
    id: { transformer: fieldTransformers.id, params: [idKey] },
    name: { transformer: fieldTransformers.name },
    description: { transformer: fieldTransformers.description },
    createdAt: { transformer: fieldTransformers.createdAt },
    status: { transformer: fieldTransformers.status, params: [customCases] },
    actions: { transformer: fieldTransformers.actions }
  }),
  
  // Department specific transformer
  department: (customCases = null) => ({
    id: { transformer: fieldTransformers.id, params: ["uuid"] },
    name: { transformer: fieldTransformers.name },
    abbreviation: { transformer: fieldTransformers.abbreviation },
    description: { transformer: fieldTransformers.description },
    createdAt: { transformer: fieldTransformers.createdAt },
    status: { transformer: fieldTransformers.status, params: [customCases] },
    actions: { transformer: fieldTransformers.actions }
  }),
  
  // User/Staff profile transformer
  userProfile: (linkPrefix = "/staff") => ({
    uuid: { transformer: fieldTransformers.id, params: ["uuid"] },
    id: { transformer: fieldTransformers.id, params: ["code"] },
    name: { 
      transformer: fieldTransformers.profile, 
      params: [{ linkPrefix }] 
    },
    phone: { transformer: fieldTransformers.phone },
    createdAt: { transformer: fieldTransformers.createdAt },
    status: { transformer: fieldTransformers.status },
    actions: { transformer: fieldTransformers.actions }
  }),
  
  // Vendor transformer
  vendor: () => ({
    id: { transformer: fieldTransformers.id, params: ["id"] },
    uuid: { transformer: fieldTransformers.id, params: ["uuid"] },
    code: { transformer: fieldTransformers.name, params: ["code"] },
    name: { transformer: fieldTransformers.name },
    abrv: { transformer: fieldTransformers.abbreviation },
    phone: { transformer: fieldTransformers.phone },
    address: { transformer: fieldTransformers.description, params: ["address"] },
    actions: { transformer: fieldTransformers.actions }
  }),
  
  // Designation transformer (with department and roles)
  designation: (customCases = null, departmentList = [], roles = []) => ({
    id: { transformer: fieldTransformers.id, params: ["uuid"] },
    name: { transformer: fieldTransformers.name },
    abrv: { transformer: fieldTransformers.abbreviation },
    department: { 
      transformer: fieldTransformers.select,
      params: ["department", departmentList]
    },
    roles: { 
      transformer: fieldTransformers.select,
      params: ["roles", roles]
    },
    description: { transformer: fieldTransformers.description },
    created_at: { transformer: fieldTransformers.createdAt },
    status: { transformer: fieldTransformers.status, params: [customCases] },
    actions: { transformer: fieldTransformers.actions }
  }),
  
  // Leave setup transformer (with allotted field)
  leaveSetup: (customCases = null) => ({
    id: { transformer: fieldTransformers.id, params: ["uuid"] },
    leave_type_id: { transformer: fieldTransformers.id, params: ["id"] },
    name: { transformer: fieldTransformers.name },
    abrv: { transformer: fieldTransformers.abbreviation },
    code: { transformer: fieldTransformers.name, params: ["code"] },
    allotted: { transformer: fieldTransformers.number, params: ["allotted"] },
    carry_forward: { transformer: fieldTransformers.number, params: ["carry_forward"] },
    accrual_type: { transformer: fieldTransformers.name, params: ["accrual_type"] },
    accrual_rate: { transformer: fieldTransformers.number, params: ["accrual_rate"] },
    requires_doc_above_days: { transformer: fieldTransformers.number, params: ["requires_doc_above_days"] },
    applicable_to: {
      transformer: (item) => createField(item?.applicable_to || item?.gender_specific || "all", "text"),
    },
    description: { transformer: fieldTransformers.description },
    created_at: { transformer: fieldTransformers.createdAt },
    // is_encashable: { transformer: fieldTransformers.is_encashable },

    is_encashable: { 
      transformer: (value) => (value == 1 ? "Yes" : "No"), 
      params: ["is_encashable"] 
    },
    status: { transformer: fieldTransformers.status, params: [customCases] },
    actions: { transformer: fieldTransformers.actions }
  })
};

/**
 * Helper function to merge custom field mappings with preset configurations
 * @param {Object} preset - Preset configuration
 * @param {Object} customFields - Custom field mappings to merge
 * @returns {Object} - Merged field mappings
 */
export const mergeTransformers = (preset, customFields = {}) => {
  return { ...preset, ...customFields };
};

/**
 * Quick transformer creator for simple cases
 * @param {Array} fields - Array of field configurations
 * @returns {Object} - Field mappings object
 * 
 * Example:
 * const mappings = quickTransformer([
 *   { name: "id", type: "id", params: ["uuid"] },
 *   { name: "name", type: "name" },
 *   { name: "status", type: "status", params: [customCases] }
 * ]);
 */
export const quickTransformer = (fields) => {
  const mappings = {};
  
  fields.forEach(field => {
    const { name, type, params = [] } = field;
    if (fieldTransformers[type]) {
      mappings[name] = {
        transformer: fieldTransformers[type],
        params
      };
    }
  });
  
  return mappings;
};

// Export default for easy importing
export default {
  createField,
  fieldTransformers,
  createDataTransformer,
  presetTransformers,
  mergeTransformers,
  quickTransformer
};
